import { useState, useEffect, useRef } from 'react';
import { Plus, Edit, Trash2, Palette, Save, X, Upload } from 'lucide-react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { uploadImage, generateImagePath } from '../services/imageUploadService';
import MapCoordinatePicker from '../components/MapCoordinatePicker';

interface Artwork {
  id: string;
  title: string;
  artist: string;
  year: string;
  description: string;
  image: string;
  location: string;
  coordinates: {
    latitude: number;
    longitude: number;
  };
  materials: string;
  dimensions: string;
  category: string;
  meaning?: string;
  quote?: string;
}

const ArtworksNew = () => {
  const [artworks, setArtworks] = useState<Artwork[]>([]);
  const [selectedArtwork, setSelectedArtwork] = useState<Artwork | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState<Omit<Artwork, 'id'>>({
    title: '',
    artist: '',
    year: '',
    description: '',
    image: '',
    location: '',
    coordinates: {
      latitude: 0,
      longitude: 0
    },
    materials: '',
    dimensions: '',
    category: '',
    meaning: '',
    quote: ''
  });

  // Dati hardcoded dall'app mobile (da sincronizzare)
  const mobileArtworks: Artwork[] = [
    {
      id: '1',
      title: 'Sentinelle del Tempo',
      artist: 'Antonio Ievolella',
      year: '2023',
      description: 'Installazione scultorea che rappresenta le sentinelle silenziose del territorio cilentano. Le figure, realizzate in pietra locale, sembrano emergere dalla terra stessa, custodi millenari di una storia che si perde nel tempo.',
      image: '/placeholder-artwork.jpg',
      location: 'Via Giordano, Casal Velino',
      coordinates: {
        latitude: 40.1691,
        longitude: 15.1430
      },
      materials: 'Pietra calcarea del Cilento',
      dimensions: '200x150x80 cm',
      category: 'Scultura',
      meaning: 'Installazione artistica che evoca la memoria storica del borgo attraverso figure stilizzate che osservano il paesaggio.',
      quote: 'Le sentinelle del tempo custodiscono i segreti di una terra antica.'
    },
    {
      id: '2',
      title: 'Memorie di Pietra',
      artist: 'Antonio Ievolella',
      year: '2022',
      description: 'Una composizione che evoca la memoria collettiva del territorio attraverso forme organiche che dialogano con la natura circostante.',
      image: '/placeholder-artwork.jpg',
      location: 'Piazza Lista, Casal Velino',
      coordinates: {
        latitude: 40.1689,
        longitude: 15.1428
      },
      materials: 'Pietra e ferro',
      dimensions: '180x120x60 cm',
      category: 'Scultura',
      meaning: 'Una composizione che evoca la memoria collettiva del territorio attraverso forme organiche che dialogano con la natura circostante.',
      quote: 'La pietra racconta storie che solo il cuore può ascoltare.'
    },
    {
      id: '3',
      title: 'Onde di Terra',
      artist: 'Antonio Ievolella',
      year: '2023',
      description: 'Installazione che richiama il movimento delle onde del mare attraverso forme sinuose che si integrano perfettamente con il paesaggio.',
      image: '/placeholder-artwork.jpg',
      location: 'Lungomare, Casal Velino',
      coordinates: {
        latitude: 40.1680,
        longitude: 15.1400
      },
      materials: 'Pietra e cemento',
      dimensions: '300x200x100 cm',
      category: 'Installazione',
      meaning: 'Installazione che richiama il movimento delle onde del mare attraverso forme sinuose che si integrano perfettamente con il paesaggio.',
      quote: 'Come le onde del mare, la terra danza al ritmo del tempo.'
    },
    {
      id: '4',
      title: 'Radici del Tempo',
      artist: 'Antonio Ievolella',
      year: '2022',
      description: 'Un\'opera che esplora il legame profondo tra l\'uomo e la terra, tra passato e presente, attraverso forme che sembrano crescere dal suolo.',
      image: '/placeholder-artwork.jpg',
      location: 'Via Roma, Casal Velino',
      coordinates: {
        latitude: 40.1695,
        longitude: 15.1435
      },
      materials: 'Pietra e legno',
      dimensions: '250x180x90 cm',
      category: 'Scultura',
      meaning: 'Un\'opera che esplora il legame profondo tra l\'uomo e la terra, tra passato e presente, attraverso forme che sembrano crescere dal suolo.',
      quote: 'Le radici del tempo affondano nel cuore della terra.'
    }
  ];

  const categories = [
    'Scultura',
    'Installazione',
    'Installazione visiva',
    'Pittura',
    'Fotografia',
    'Arte Digitale',
    'Performance',
    'Mixed Media'
  ];

  useEffect(() => {
    loadArtworks();
  }, []);

  const loadArtworks = async () => {
    try {
      setIsLoading(true);
      const artworksCol = collection(db, 'artworks');
      const snapshot = await getDocs(artworksCol);
      const artworksData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Artwork[];
      const sortedArtworks = [...artworksData].sort((a, b) =>
        (a.title || '').localeCompare(b.title || '', 'it', { sensitivity: 'base' })
      );
      
      // Se non ci sono opere nel database, usa quelle hardcoded
      if (artworksData.length === 0) {
        console.log('Nessuna opera trovata in Firestore, usando dati hardcoded');
        setArtworks(
          [...mobileArtworks].sort((a, b) =>
            (a.title || '').localeCompare(b.title || '', 'it', { sensitivity: 'base' })
          )
        );
      } else {
        console.log(`Caricate ${artworksData.length} opere da Firestore`);
        setArtworks(sortedArtworks);
      }
    } catch (error) {
      console.error('Error loading artworks:', error);
      // Fallback alle opere hardcoded
      setArtworks(mobileArtworks);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      // Genera un ID temporaneo per nuove opere
      const artworkId = selectedArtwork?.id || `temp_${Date.now()}`;
      const imagePath = generateImagePath('artworks', artworkId, file.name);
      const imageUrl = await uploadImage(file, imagePath);
      setFormData(prev => ({ ...prev, image: imageUrl }));
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Errore durante il caricamento dell\'immagine');
    } finally {
      setIsUploading(false);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleSelectArtwork = (artwork: Artwork) => {
    setSelectedArtwork(artwork);
    setFormData({
      title: artwork.title,
      artist: artwork.artist,
      year: artwork.year,
      description: artwork.description,
      image: artwork.image,
      location: artwork.location,
      coordinates: artwork.coordinates,
      materials: artwork.materials,
      dimensions: artwork.dimensions,
      category: artwork.category,
      meaning: artwork.meaning || '',
      quote: artwork.quote || ''
    });
    setIsEditing(false);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleNew = () => {
    setSelectedArtwork(null);
    setFormData({
      title: '',
      artist: '',
      year: '',
      description: '',
      image: '',
      location: '',
      coordinates: {
        latitude: 0,
        longitude: 0
      },
      materials: '',
      dimensions: '',
      category: '',
      meaning: '',
      quote: ''
    });
    setIsEditing(true);
  };


  const handleSave = async () => {
    try {
      if (!formData.title.trim()) {
        alert('Inserisci il titolo dell\'opera.');
        return;
      }
      setIsLoading(true);
      if (selectedArtwork) {
        await updateDoc(doc(db, 'artworks', selectedArtwork.id), formData);
        setArtworks(prev => prev.map(a => a.id === selectedArtwork.id ? { ...a, ...formData } : a));
        setSelectedArtwork({ ...selectedArtwork, ...formData });
      } else {
        const newArtwork = await addDoc(collection(db, 'artworks'), formData);
        const newArt = { id: newArtwork.id, ...formData };
        setArtworks(prev => [...prev, newArt].sort((a, b) =>
          (a.title || '').localeCompare(b.title || '', 'it', { sensitivity: 'base' })
        ));
        setSelectedArtwork(newArt);
      }
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving artwork:', error);
      alert('Errore durante il salvataggio dell\'opera');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (selectedArtwork) {
      setFormData({
        title: selectedArtwork.title,
        artist: selectedArtwork.artist,
        year: selectedArtwork.year,
        description: selectedArtwork.description,
        image: selectedArtwork.image,
        location: selectedArtwork.location,
        coordinates: selectedArtwork.coordinates,
        materials: selectedArtwork.materials,
        dimensions: selectedArtwork.dimensions,
        category: selectedArtwork.category,
        meaning: selectedArtwork.meaning || '',
        quote: selectedArtwork.quote || ''
      });
    } else {
      setFormData({
        title: '',
        artist: '',
        year: '',
        description: '',
        image: '',
        location: '',
        coordinates: {
          latitude: 0,
          longitude: 0
        },
        materials: '',
        dimensions: '',
        category: '',
        meaning: '',
        quote: ''
      });
    }
    setIsEditing(false);
  };

  const handleDelete = async (artworkId: string) => {
    if (window.confirm('Sei sicuro di voler eliminare questa opera?')) {
      try {
        await deleteDoc(doc(db, 'artworks', artworkId));
        setArtworks(prev => prev.filter(a => a.id !== artworkId));
        if (selectedArtwork?.id === artworkId) {
          setSelectedArtwork(null);
        }
      } catch (error) {
        console.error('Error deleting artwork:', error);
      }
    }
  };


  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-primary text-white p-6">
        <div className="container-max">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Palette size={24} />
              <h1 className="text-2xl font-bold">Gestione Opere d'Arte</h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={handleNew}
                className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center space-x-2"
              >
                <Plus size={16} />
                <span>Nuova Opera</span>
              </button>
            </div>
          </div>
          <p className="text-white/80 mt-2">
            Gestisci opere d'arte e installazioni visive del percorso
          </p>
        </div>
      </div>

      <div className="container-max p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Lista Opere */}
          <div className="lg:col-span-1 sticky top-6 self-start">
            <div className="bg-white rounded-lg shadow-sm border border-border">
              <div className="p-4 border-b border-border">
                <h2 className="text-lg font-semibold text-text-primary">Opere Disponibili</h2>
              </div>
              <div className="max-h-[calc(100vh-220px)] min-h-[600px] overflow-y-auto pr-1">
                {isLoading ? (
                  <div className="p-4 space-y-3">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="flex items-center space-x-3 p-3 animate-pulse">
                        <div className="w-16 h-16 bg-gray-200 rounded-lg"></div>
                        <div className="flex-1">
                          <div className="h-4 bg-gray-200 rounded mb-2"></div>
                          <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-2">
                    {artworks.map((artwork) => (
                      <div
                        key={artwork.id}
                        onClick={() => handleSelectArtwork(artwork)}
                        className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors duration-200 ${
                          selectedArtwork?.id === artwork.id
                            ? 'bg-primary/10 border border-primary/20'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="w-16 h-16 bg-gray-200 rounded-lg overflow-hidden">
                          <img 
                            src={artwork.image || '/placeholder-artwork.jpg'} 
                            alt={artwork.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-text-primary truncate">
                            {artwork.title}
                          </h3>
                          <p className="text-sm text-text-secondary truncate">
                            {artwork.artist} - {artwork.year}
                          </p>
                          <p className="text-xs text-text-secondary truncate">
                            {artwork.location}
                          </p>
                        </div>
                        <div className="flex space-x-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectArtwork(artwork);
                              handleEdit();
                            }}
                            className="p-1 text-text-secondary hover:text-primary transition-colors duration-200"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(artwork.id);
                            }}
                            className="p-1 text-text-secondary hover:text-error transition-colors duration-200"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Form di Editing */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-border">
              {selectedArtwork || isEditing ? (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-16 h-16 bg-gray-200 rounded-lg overflow-hidden">
                        <img 
                          src={formData.image || selectedArtwork?.image || '/placeholder-artwork.jpg'} 
                          alt={formData.title || selectedArtwork?.title || 'Nuova Opera'}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <h2 className="text-xl font-semibold text-text-primary">
                          {formData.title || selectedArtwork?.title || 'Nuova Opera'}
                        </h2>
                        <p className="text-text-secondary">
                          {formData.artist || selectedArtwork?.artist || 'Antonio Ievolella'} {formData.year || selectedArtwork?.year ? `- ${formData.year || selectedArtwork?.year}` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      {isEditing ? (
                        <>
                          <button
                            onClick={handleSave}
                            className="flex items-center space-x-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors duration-200"
                          >
                            <Save size={16} />
                            <span>Salva</span>
                          </button>
                          <button
                            onClick={handleCancel}
                            className="flex items-center space-x-2 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors duration-200"
                          >
                            <X size={16} />
                            <span>Annulla</span>
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={handleEdit}
                          className="flex items-center space-x-2 bg-secondary text-white px-4 py-2 rounded-lg hover:bg-secondary/90 transition-colors duration-200"
                        >
                          <Edit size={16} />
                          <span>Modifica</span>
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="space-y-6">
                    {/* Informazioni Base */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          Titolo *
                        </label>
                        <input
                          type="text"
                          value={formData.title}
                          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          Artista *
                        </label>
                        <input
                          type="text"
                          value={formData.artist}
                          onChange={(e) => setFormData({ ...formData, artist: e.target.value })}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          Anno *
                        </label>
                        <input
                          type="text"
                          value={formData.year}
                          onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          Categoria *
                        </label>
                        <select
                          value={formData.category}
                          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                        >
                          <option value="">Seleziona categoria</option>
                          {categories.map((category) => (
                            <option key={category} value={category}>
                              {category}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          Dimensioni
                        </label>
                        <input
                          type="text"
                          value={formData.dimensions}
                          onChange={(e) => setFormData({ ...formData, dimensions: e.target.value })}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                          placeholder="es. 200x150x80 cm"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Descrizione *
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        disabled={!isEditing}
                        rows={4}
                        className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Materiali
                      </label>
                      <input
                        type="text"
                        value={formData.materials}
                        onChange={(e) => setFormData({ ...formData, materials: e.target.value })}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                        placeholder="es. Pietra calcarea del Cilento"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Significato
                      </label>
                      <textarea
                        value={formData.meaning}
                        onChange={(e) => setFormData({ ...formData, meaning: e.target.value })}
                        disabled={!isEditing}
                        rows={4}
                        className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                        placeholder="Il significato profondo dell'opera e il messaggio che vuole trasmettere..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Citazione
                      </label>
                      <textarea
                        value={formData.quote}
                        onChange={(e) => setFormData({ ...formData, quote: e.target.value })}
                        disabled={!isEditing}
                        rows={3}
                        className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                        placeholder="Una citazione o frase evocativa legata all'opera..."
                      />
                    </div>

                    {/* Immagine */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Immagine
                      </label>
                      <div className="flex items-center space-x-4">
                        <div className="w-24 h-24 bg-gray-200 rounded-lg overflow-hidden">
                          <img 
                            src={formData.image || '/placeholder-artwork.jpg'} 
                            alt="Preview"
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1">
                          <input
                            type="url"
                            value={formData.image}
                            onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                            disabled={!isEditing}
                            className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                            placeholder="URL dell'immagine"
                          />
                          {isEditing && (
                            <>
                              <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleImageUpload}
                                className="hidden"
                              />
                              <button 
                                type="button"
                                onClick={handleUploadClick}
                                disabled={isUploading}
                                className="mt-2 flex items-center space-x-2 text-primary hover:text-secondary transition-colors duration-200 disabled:opacity-50"
                              >
                                <Upload size={16} />
                                <span className="text-sm">
                                  {isUploading ? 'Caricamento...' : 'Carica immagine'}
                                </span>
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Posizione */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Luogo *
                      </label>
                      <input
                        type="text"
                        value={formData.location}
                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                        placeholder="es. Via Giordano, Casal Velino"
                      />
                    </div>

                    {/* Coordinate con Google Maps */}
                    <MapCoordinatePicker
                      latitude={formData.coordinates.latitude}
                      longitude={formData.coordinates.longitude}
                      onChange={(lat, lng) => setFormData({
                        ...formData,
                        coordinates: { latitude: lat, longitude: lng }
                      })}
                      disabled={!isEditing}
                    />
                  </div>
                </div>
              ) : (
                <div className="p-12 text-center">
                  <Palette size={48} className="mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-text-primary mb-2">
                    Seleziona un'Opera
                  </h3>
                  <p className="text-text-secondary">
                    Scegli un'opera dalla lista per modificarla o creane una nuova
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArtworksNew;
